import { Link } from '../../router/Link';
import {
  Button,
  ConfirmButton,
  Field,
  LoadingStatus,
  Notice,
  Panel,
  PlaylistsIcon,
  ProblemPanel,
  Spinner,
  StatePanel,
  Thumbnail,
  cx,
} from '../../ui';
import { FeedGridSkeleton } from '../../listing/FeedGrid';
import { GridSearch } from '../../listing/GridSearch';
import { shiftRangePress } from '../../listing/selectPress';
import { useGridFocus } from '../../listing/useGridFocus';
import { privacyDetail, privacyLabel } from '../../playlists/model';
import { useCopy } from '../../strings/useCopy';
import { usePlaylists } from './usePlaylists';
import type { OwnedPlaylistRow } from '../../playlists/model';
import type { PlaylistsFormState } from './model';
import layout from '../../ui/layout.module.css';
import styles from './playlists.module.css';

const PRIVACIES = ['public', 'unlisted', 'private'] as const;

/**
 * §8's Playlists page. Renders props (R13) — every href arrived built, every string arrived formatted.
 *
 * Manage mode: the Manage button swaps the page from browsing to acting. Managing shows the
 * create form (creation is management, so it lives here now rather than always-on), turns every card
 * into a selection, and puts the two bulk acts — delete, and re-set who can see them — in a bar above
 * the grid. Deleting confirms in place (`ConfirmButton`); it is the one act on this page with no way back.
 *
 * `layout.wide` is the frame and the cards are the feed grid's, both for the reason `SubscriptionsView`
 * spells out: the gate renders its children verbatim, so a page behind it that sets no frame gets none.
 */
export function PlaylistsView() {
  const binding = usePlaylists();
  const { state } = binding;
  const copy = useCopy().text;
  const words = copy.playlists.page;
  const searchWords = copy.listing.search;

  return (
    <div className={layout.wide}>
      <section className={styles.page} aria-busy={state.kind === 'loading' ? true : undefined}>
        <header className={styles.headRow}>
          <div className={styles.head}>
            <h1 className={styles.heading}>
              {words.heading}
              {binding.refreshing ? (
                <span className={styles.refreshing}>
                  <Spinner />
                </span>
              ) : null}
            </h1>
            <p className={styles.lede}>{words.lede}</p>
          </div>
          <Button variant="default" size="sm" onClick={binding.toggleManage} ariaPressed={binding.manage}>
            {binding.manage ? words.manage.leave : words.manage.enter}
          </Button>
        </header>

        {binding.manage ? <CreateForm form={binding.form} binding={binding} /> : null}

        {/* The controls line above the grid (CLAUDE.md): the bulk acts (managing) on the
            left, the search on the right — one line, so entering manage mode adds no second row. */}
        {binding.manage || state.kind === 'ready' ? (
          <div className={styles.controls}>
            {binding.manage ? <ManageBar binding={binding} /> : null}
            <GridSearch
              id="search-playlists"
              value={binding.search}
              onChange={binding.setSearch}
              words={searchWords}
              className={styles.controlsSearch}
            />
          </div>
        ) : null}

        {binding.manageProblem === null ? null : (
          <Notice tone="caution" live>
            <strong>{binding.manageProblem.title}.</strong> {binding.manageProblem.detail}
          </Notice>
        )}

        {state.kind === 'loading' ? (
          <>
            <LoadingStatus>{words.loading}</LoadingStatus>
            {/*
             * The feed grid's own skeleton, so the cards land where the boxes were. Four rather than a
             * number from the model: this endpoint has no count to page by — it answers with the whole
             * collection in one request — so there is nothing to know here beyond "about one row".
             */}
            <FeedGridSkeleton cards={4} />
          </>
        ) : null}

        {state.kind === 'problem' ? <ProblemPanel copy={state.copy} onRetry={binding.retry} /> : null}

        {state.kind === 'ready' ? (
          <>
            {state.empty ? (
              <StatePanel title={words.empty.title} detail={words.empty.detail} />
            ) : state.nothingMatches ? (
              <p className={styles.noMatches}>{searchWords.noMatches(binding.search.trim())}</p>
            ) : (
              <PlaylistGrid rows={state.rows} binding={binding} />
            )}

            {state.notices.map((notice) => (
              <Notice key={notice.id} tone="info" className={styles.notice}>
                {notice.text}
              </Notice>
            ))}
          </>
        ) : null}
      </section>
    </div>
  );
}

/**
 * The two bulk acts over the selection. Deletion is the page's one irreversible act, so it still
 * costs two clicks — the arming moved into the button itself (`ConfirmButton`, the app-wide
 * deletion grammar), and the no-undo sentence rides the armed state's title.
 */
function ManageBar({ binding }: { readonly binding: ReturnType<typeof usePlaylists> }) {
  const copy = useCopy().text;
  const words = copy.playlists.page.manage;
  const count = binding.selected.size;
  const idle = binding.working === null;

  return (
    <div className={styles.manageBar}>
      <ConfirmButton
        variant="default"
        size="sm"
        disabled={count === 0 || !idle}
        confirmLabel={copy.shell.confirm}
        confirmTitle={words.confirmDetail}
        onConfirm={binding.deleteSelected}
      >
        {idle ? words.deleteSelected : words.working(binding.working.done, binding.working.total)}
      </ConfirmButton>
      <span className={styles.manageDivider} aria-hidden="true" />
      <Button
        variant="default"
        size="sm"
        disabled={count === 0 || !idle}
        onClick={() => binding.setPrivacySelected('public')}
      >
        {words.makePublic}
      </Button>
      <Button
        variant="default"
        size="sm"
        disabled={count === 0 || !idle}
        onClick={() => binding.setPrivacySelected('unlisted')}
      >
        {words.makeUnlisted}
      </Button>
      <Button
        variant="default"
        size="sm"
        disabled={count === 0 || !idle}
        onClick={() => binding.setPrivacySelected('private')}
      >
        {words.makePrivate}
      </Button>
      {/* The selection's size lives here, not in the buttons — always rendered, so the bar
          holds its shape when the first card is ticked and the zero explains the disabled row. */}
      <span className={styles.selectedCount}>{words.selectedCount(count)}</span>
      {/* The way out of a selection that isn't a deselection per card — ghost, because it
          changes nothing on the instance. */}
      <Button
        variant="ghost"
        size="sm"
        disabled={count === 0 || !idle}
        onClick={binding.clearSelection}
      >
        {words.clearSelection}
      </Button>
    </div>
  );
}

function CreateForm({
  form,
  binding,
}: {
  readonly form: PlaylistsFormState;
  readonly binding: ReturnType<typeof usePlaylists>;
}) {
  const words = useCopy().text.playlists.page.create;
  const detail = privacyDetail();

  return (
    <Panel as="section" ariaLabel={words.sectionLabel} className={styles.create}>
      <h2 className={styles.createHeading}>{words.heading}</h2>
      <div className={styles.createRow}>
        <Field
          id="new-playlist-title"
          label={words.titleLabel}
          value={form.title}
          onChange={binding.setTitle}
          onSubmit={binding.submit}
          placeholder={words.titlePlaceholder}
          problem={form.field.problem}
          hint={form.field.hint}
          className={styles.createField}
        />
        <div className={styles.createPrivacy}>
          <span className={styles.createPrivacyLabel} id="new-playlist-privacy">
            {words.privacyLabel}
          </span>
          <div className={styles.privacyGroup} role="radiogroup" aria-labelledby="new-playlist-privacy">
            {PRIVACIES.map((privacy) => (
              <button
                key={privacy}
                type="button"
                role="radio"
                aria-checked={form.privacy === privacy}
                className={styles.privacyOption}
                title={detail[privacy]}
                onClick={() => binding.setPrivacy(privacy)}
              >
                {privacyLabel()[privacy]}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.createAction}>
          <Button variant="primary" onClick={binding.submit} disabled={!form.canSubmit}>
            {form.busy ? words.submitting : words.submit}
          </Button>
        </div>
      </div>
      <p className={styles.createHelp}>{detail[form.privacy]}</p>
      {form.problem === null ? null : (
        <Notice tone="caution" live>
          <strong>{form.problem.title}.</strong> {form.problem.detail}
        </Notice>
      )}
    </Panel>
  );
}

/**
 * The collection, in the same grid Home and Trending draw — `listing.module.css`'s `.grid`, composed
 * rather than copied, so the measured column arithmetic (`auto-fill` over `--vs-layout-feed-card-min`)
 * stays in one place and this page cannot drift out of step with it.
 *
 * §9's roving focus comes with the grid rather than being a nicety on top of it: `useGridFocus` measures
 * the column count the browser resolved, which is the only thing that makes `↓` land one row down here
 * as well, and it gives the whole collection one tab stop — the checkbox in manage mode, the title link
 * otherwise.
 */
function PlaylistGrid({
  rows,
  binding,
}: {
  readonly rows: readonly OwnedPlaylistRow[];
  readonly binding: ReturnType<typeof usePlaylists>;
}) {
  const { attachGrid, onKeyDown, tabIndexFor, onFocusIndex } = useGridFocus(rows.length);
  const listLabel = useCopy().text.playlists.page.listLabel;

  return (
    <ul
      className={styles.grid}
      aria-label={listLabel}
      ref={attachGrid}
      onKeyDown={onKeyDown}
    >
      {rows.map((row, index) => (
        <PlaylistCard
          key={row.playlistId}
          row={row}
          tabIndex={tabIndexFor(index)}
          onFocus={() => onFocusIndex(index)}
          selectable={
            binding.manage
              ? {
                  selected: binding.selected.has(row.playlistId),
                  onToggle: (range) => binding.toggleSelect(row.playlistId, range),
                }
              : null
          }
        />
      ))}
    </ul>
  );
}

/**
 * One owned playlist as a card.
 *
 * Not `listing/PlaylistCard`, and the difference is what the card has to say rather than how it looks: a
 * search result's playlist card states an *author*, which is the fact that matters about somebody else's
 * playlist and is the one fact that is never worth stating about your own. What goes there instead is the
 * pair only the owner can act on — who can see it, and when it last changed.
 *
 * Two shapes now. Browsing, it is two anchors arranged the way `ListingCard` arranges its three (an
 * anchor inside an anchor is invalid HTML that browsers resolve by silently dropping one, so the frame's
 * copy is `aria-hidden` + `tabIndex={-1}` and the title is the one real link). Managing, the whole card
 * is one `<label>` around a checkbox — no links at all, because a card that both selects and navigates
 * loses a playlist to a mis-click.
 */
function PlaylistCard({
  row,
  tabIndex,
  onFocus,
  selectable,
}: {
  readonly row: OwnedPlaylistRow;
  readonly tabIndex: number;
  readonly onFocus: () => void;
  readonly selectable: {
    readonly selected: boolean;
    /** `range` is a held Shift — the previously toggled card to this one, inclusive. */
    readonly onToggle: (range: boolean) => void;
  } | null;
}) {
  const words = useCopy().text.playlists.page;

  const frame = (
    <div className={styles.frame}>
      {selectable === null ? (
        <Link className={styles.cover} href={row.href} tabIndex={-1} ariaHidden>
          <Cover row={row} />
        </Link>
      ) : (
        <span className={styles.cover}>
          <Cover row={row} />
        </span>
      )}
      {row.countText === null ? null : <span className={styles.count}>{row.countText}</span>}
      {selectable === null ? null : (
        <input
          type="checkbox"
          className={styles.select}
          checked={selectable.selected}
          /*
           * This reads the modifier for a press that landed **on the box itself**, where the
           * control's own `change` fires in both engines and carries it. Everywhere else on the
           * card the label's click is what answers a shifted press — no engine forwards that one
           * dependably (`listing/selectPress.ts`).
           */
          onChange={(event) => {
            selectable.onToggle(
              event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey,
            );
          }}
          aria-label={words.manage.selectLabel(row.title)}
          tabIndex={tabIndex}
        />
      )}
    </div>
  );

  const meta = (
    <div className={styles.meta}>
      {/*
        * A dotted underline where the label is not exact, rather than a "?" character — which read
        * as a typo in the first browser pass. The `title` is the explanation; the underline is the
        * conventional signal that there is one.
        */}
      <span
        className={row.visibility.caveat === null ? styles.badge : styles.badgeInexact}
        title={row.visibility.title}
      >
        {row.visibility.label}
      </span>
      {row.updatedText === null ? null : <span>{words.updated(row.updatedText)}</span>}
    </div>
  );

  if (selectable !== null) {
    return (
      <li className={cx(styles.card, selectable.selected && styles.cardSelected)} onFocus={onFocus}>
        {/* The shifted press is ours (2026-08-02) — no engine forwards it to the checkbox
            reliably, and `listing/selectPress.ts` has both measurements. */}
        <label className={styles.selectWrap} onClick={shiftRangePress(selectable.onToggle)}>
          {frame}
          <div className={styles.text}>
            <span className={row.untitled ? styles.titleUntitled : styles.title} title={row.title}>
              {row.title}
            </span>
            {meta}
          </div>
        </label>
      </li>
    );
  }

  return (
    <li className={styles.card} onFocus={onFocus}>
      {frame}
      <div className={styles.text}>
        <Link
          className={row.untitled ? styles.titleUntitled : styles.title}
          href={row.href}
          title={row.title}
          tabIndex={tabIndex}
        >
          {row.title}
        </Link>
        {meta}
      </div>
    </li>
  );
}

/**
 * The frame's picture, a collage: a 2×2 of the first entries' own thumbnails where the embedded
 * page carries at least two, the one entry alone where that is all there is, and the glyph where the
 * playlist is empty — a bare grey rectangle in a grid reads as an image that did not arrive.
 */
function Cover({ row }: { readonly row: OwnedPlaylistRow }) {
  switch (row.cover.kind) {
    case 'none':
      return (
        <span className={styles.coverEmpty} aria-hidden="true">
          <PlaylistsIcon />
        </span>
      );
    case 'single':
      return <Thumbnail src={row.cover.url} />;
    case 'collage':
      return (
        <span className={styles.collage} data-tiles={row.cover.urls.length} aria-hidden="true">
          {row.cover.urls.map((url) => (
            <img key={url} className={styles.collageTile} src={url} alt="" loading="lazy" decoding="async" />
          ))}
        </span>
      );
  }
}
